import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PersonIcon from "@mui/icons-material/Person";
import { SubmitOnlinePay } from "./SubmitOnlinePay";
import DownloadIcon from "@mui/icons-material/Download";
import SecurePdfViewer from "../SecurePdfViewer";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);
  return `${day}/${month}/${year}`;
};

export const PaymentReceiptView = ({ responseData, formatter }) => {
  const datosOpag = responseData?.ES_OPAG || {};

  const handleDowloadPDF = () => {
    if (datosOpag.FORMATO) {
      window.open(`${datosOpag.FORMATO}/2`, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Card sx={{ borderLeft: "6px solid var(--main-color-vino)", mb: 3 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "var(--main-color-vino)" }}
              >
                ¡Línea de captura generada con éxito!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Orden de pago N°: <strong>{datosOpag.NRO_ORD_PAGO}</strong>
              </Typography>
            </Box>
            <Box textAlign={{ xs: "left", sm: "right" }}>
              <Typography variant="body2" color="text.secondary">
                Total a pagar
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: "#2e7d32" }}
              >
                {formatter.format(Number(datosOpag.IMPORTE || 0))}{" "}
                {datosOpag.MONEDA}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent
              sx={{ display: "flex", flexDirection: "column", height: "100%" }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <PersonIcon sx={{ color: "var(--main-color-vino)" }} />
                <Typography variant="h6" sx={{ fontWeight: "600" }}>
                  Datos generales
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Box
                display="flex"
                flexDirection="column"
                gap={1.5}
                sx={{ flexGrow: 1 }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    LÍNEA DE CAPTURA
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      letterSpacing: "1px",
                      color: "#1565c0",
                    }}
                  >
                    {datosOpag.LINEA_CAPTURA}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    CONTRIBUYENTE
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "500" }}>
                    {datosOpag.NOMBRE}
                  </Typography>
                </Box>

                <Grid container spacing={1}>
                  <Grid item xs={6} sx={{ textAlign: "center" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      RFC
                    </Typography>
                    <Typography variant="body2">{datosOpag.RFC}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "center" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      VENCIMIENTO
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#c62828", fontWeight: "500" }}
                    >
                      {datosOpag.FECHAPLINEA ||
                        formatDate(datosOpag.FECHA_VENCIMIENTO)}
                    </Typography>
                  </Grid>
                </Grid>

                {datosOpag.DIRECCION && (
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      DOMICILIO
                    </Typography>
                    <Typography variant="body2">
                      {datosOpag.DIRECCION}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <SubmitOnlinePay data={datosOpag} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                flexGrow: 1,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <ReceiptLongIcon sx={{ color: "var(--main-color-vino)" }} />
                  <Typography variant="h6" sx={{ fontWeight: "600" }}>
                    Formato de pago
                  </Typography>
                </Box>

                {datosOpag.FORMATO && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDowloadPDF}
                    sx={{
                      backgroundColor: "var(--main-color-vino)",
                      padding: "12px",
                      fontWeight: "700",
                      borderRadius: "8px",
                      boxShadow: "0 2px 4px rgba(128, 0, 32, 0.2)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "var(--main-color-vino)",
                        boxShadow: "0 6px 16px rgba(128, 0, 32, 0.35)",
                        transform: "translateY(-3px)",
                      },
                      "&:active": {
                        transform: "translateY(1px)",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    Descargar formato de pago
                  </Button>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box
                sx={{
                  width: "100%",
                  height: "680px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  flexGrow: 1,
                }}
              >
                {datosOpag.FORMATO && (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      position: "relative",
                      textAlign: "center",
                    }}
                  >
                    <SecurePdfViewer url={datosOpag.FORMATO} />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
